// client/src/components/bank/BidDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatInterestRate, formatDuration, formatCurrencyWithSymbol, formatBaseRateDisplay } from '../utils/formatUtils';
import { Info } from 'lucide-react';

const BidDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Helper function to determine the rate type
  const getRateType = (bid) => {
    // First check if the bid has a rateType property
    if (bid.rateType) {
      return bid.rateType;
    }
    // Otherwise check if the request has a rateType property
    else if (bid.request?.rateType) {
      return bid.request.rateType;
    }
    // Default to fixed if no rate type is found
    else {
      return 'fixed';
    }
  };

  //Helper function to format base rate
  const formatBaseRate = (baseRate) => {
  return formatBaseRateDisplay(baseRate, t);
  };
  
  useEffect(() => {
    const fetchBidDetails = async () => {
      try {
        // Get bank bids
        const bidsRes = await axios.get('/api/bids/bank');
        
        // Find the specific bid
        const foundBid = bidsRes.data.bids.find(b => b.id === id);
        
        if (!foundBid) {
          setError(t('common.bidNotFound'));
          setLoading(false);
          return;
        }
                
        setBid(foundBid);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching bid details:');
        setError(t('common.errors.fetchBidDetails'));
        setLoading(false);
      }
    };
    
    fetchBidDetails();
  }, [id, t]);
  
  const confirmTransfer = async () => {
    try {
      if (!bid.allocation?.id) {
        setError('Allocation information is missing');
        return;
      }
      
      await axios.post(`/api/bids/transfer/${bid.allocation.id}`);
      
      // Refresh bid data
      const bidsRes = await axios.get('/api/bids/bank');
      const updatedBid = bidsRes.data.bids.find(b => b.id === id);
      setBid(updatedBid);
    } catch (err) {
      setError(t('common.errors.confirmTransfer'));
    }
  };
  
  const cancelBid = async () => {
    if (window.confirm(t('bids.bidCancelConfirm'))) {
      try {
        await axios.post(`/api/bids/cancel/${id}`);
        
        // Refresh bid data after cancellation
        const bidsRes = await axios.get('/api/bids/bank');
        const updatedBid = bidsRes.data.bids.find(b => b.id === id);
        setBid(updatedBid);
        
        // Show success message
        setSuccessMessage(t('bids.bidCancelledSuccess'));
        
        // Hide success message after a few seconds
        setTimeout(() => {
          setSuccessMessage('');
          // Navigate back to open requests after the success message is shown
          navigate('/bank/open-requests');
        }, 3000);
      } catch (err) {
        console.error('Error cancelling bid:');
        setError(t('common.errors.cancelBid') + ': ' + (err.response?.data?.message || err.message));
      }
    }
  };
  
  /* Helper function to determine if company name should be shown
  const shouldShowCompanyName = () => {
    return bid.status === 'approved' && 
           bid.allocation?.transferStatus === 'completed' && 
           bid.request?.company?.name;
  };*/
  
  const getStatusClass = () => {
    if (bid.status === 'approved') {
      return 'status-approved';
    } else if (bid.status === 'rejected') {
      return 'status-rejected';
    } else if (bid.status === 'cancelled') {
      return 'status-cancelled';
    } else {
      return 'status-pending';
    }
  };
  
  const getBackUrl = () => {
    if (bid.status === 'pending') {
      return '/bank/my-bids';
    } else {
      // All other statuses (rejected, cancelled, approved) go to past bids
      return '/bank/past-bids';
    }
  };

  const getBackButtonText = () => {
    if (bid.status === 'pending') {
      return t('bids.backToPendingBids');
    } else {
      // All other statuses (rejected, cancelled, approved) go to past bids
      return t('bids.backToPastBids');
    }
  };
  
  // Helper function to safely get allocation amount
  const getAllocationAmount = () => {
    // Check for allocation amount in different possible locations
    if (bid.allocation && typeof bid.allocation.amount !== 'undefined') {
      return bid.allocation.amount;
    } else if (typeof bid.allocatedAmount !== 'undefined') {
      return bid.allocatedAmount;
    } else {
      return 0;
    }
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingBidDetails')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  if (!bid) {
    return <div className="error-message">{t('common.bidNotFound')}</div>;
  }
  
  // Get the rate type
  const rateType = getRateType(bid);
  
  // Check if the bid has a valid request
  if (!bid.request) {
    return (
      <div className="bid-detail company-dashboard">
        <div className="card">
          <div className="card-title">
            <span>
              <Info className="card-title-icon" size={20} style={{marginRight: '8px'}}/>
              {t('bids.bidDetails')}
            </span>
            <div>
              <span className={`status ${getStatusClass()}`}>
                {bid.status === 'pending' ? t('bids.pending') :
                 bid.status === 'approved' ? t('bids.approved') :
                 bid.status === 'rejected' ? t('bids.rejected') :
                 t('bids.cancelled')}
              </span>
            </div>
          </div>
          
          <div className="alert-warning">
            <p>This bid's associated request has been deleted from the system.</p>
          </div>
          

          <div className="bid-info">
            <h3><h3>{t('bids.bidInformation')}</h3></h3>
            
            <div className="info-grid">
              <div className="info-card">
                <div className="info-card-label">{t('requests.requestId')}</div>
                <div className="info-card-value">N/A (Request deleted)</div>
              </div>
              
              <div className="info-card amount">
                <div className="info-card-label">{t('bids.bidAmount')}</div>
                <div className="info-card-value">{formatCurrencyWithSymbol(bid.amount, bid.currency || 'EUR')}</div>
              </div>
              
              <div className="info-card">
                <div className="info-card-label">{t('bids.minimumAllocation')}</div>
                <div className="info-card-value">{formatCurrencyWithSymbol(bid.minimumAllocation, bid.currency || 'EUR')}</div>
              </div>
              
              <div className="info-card">
                <div className="info-card-label">{t('requests.interestRateType')}</div>
                <div className="info-card-value">
                  {rateType === 'floating' ? t('requests.floating') : t('requests.fixed')}
                </div>
              </div>
              
              <div className="info-card">
                <div className="info-card-label">
                  {rateType === 'floating' ? t('bids.interestRateFloating').replace(' (%)', '') : t('bids.interestRate')}
                </div>
                <div className="info-card-value">
                  {formatInterestRate(bid.interestRate, rateType, bid.baseRate)}
                </div>
              </div>
              
              {/* Show base rate only if it exists and rate is floating */}
              {rateType === 'floating' && bid.baseRate && (
                <div className="info-card">
                  <div className="info-card-label">{t('bids.baseRate')}</div>
                  <div className="info-card-value">{formatBaseRate(bid.baseRate)}</div>
                </div>
              )}
              
              <div className="info-card">
                <div className="info-card-label">{t('bids.submissionDate')}</div>
                <div className="info-card-value">
                  {new Date(bid.createdAt).toLocaleDateString()}
                </div>
              </div>
              
              {bid.notes && (
                <div className="info-card notes-section">
                  <div className="info-card-label">{t('requests.additionalNotes')}</div>
                  <div className="info-card-value">{bid.notes}</div>
                </div>
              )}
            </div>
          </div>
          
          <div className="bid-actions">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => navigate('/bank/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bid-detail company-dashboard">
        {/* Navigation button outside and above the card */}
          <div className="page-navigation" style={{ marginBottom: '14px' }}>
          <button
            type="button"
            className="back-btn"
            onClick={() => navigate(getBackUrl())}
          >
          ← {getBackButtonText()}
          </button>
          </div>
      <div className="card">
        <div className="card-title">
          <span>
            <Info className="card-title-icon" size={20} style={{marginRight: '8px'}}/>
            {t('bids.bidDetails')}
            </span>
          <div>
            <span className={`status ${getStatusClass()}`}>
              {bid.status === 'pending' ? t('bids.pending') :
               bid.status === 'approved' ? t('bids.approved') :
               bid.status === 'rejected' ? t('bids.rejected') :
               t('bids.cancelled')}
            </span>
          </div>
        </div>
        
        {/* Display success message if available */}
        {successMessage && (
          <div className="alert-success" style={{ marginTop: '15px', marginBottom: '15px' }}>
            <p>{successMessage}</p>
          </div>
        )}

        <div className="bid-info">
          {/* Primo box - Richiedente con ID e data */}
          <div className="allocation-details-card">
            <div style={{ display: 'flex', gap: '40px', justifyContent: 'center' }}>
              <div>
                <strong>{t('requests.requestId')}:</strong> #{bid.request.requestId}
              </div>
              <div>
                <strong>{t('requests.company')}:</strong> {bid.request?.company?.name || t('bids.notAvailable')}
              </div>
              <div>
                <strong>{t('profile.vatNumber')}:</strong> {bid.request?.company?.vatNumber || t('profile.vatNumberNotSpecified')}
              </div>
              <div>
                <strong>{t('bids.submissionDate')}:</strong> {new Date(bid.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Secondo box - Dettagli della richiesta (formato identico a Dettagli Allocazione) */}
          <div className="bank-bid" style={{ marginBottom: '25px', marginTop: '20px'}}>
            <h3 style={{fontSize: '20px'}}>{t('requests.requestDetails')}</h3>
            <div className="detail-item">
              <div className="detail-label">{t('requests.type')}:</div>
              <div className="detail-value">
                <span className="status" style={bid.request?.requestType === 'hot_money'
                  ? { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }
                  : { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' }}>
                  {bid.request?.requestType === 'hot_money' ? t('requests.hotMoney') : t('requests.allocation')}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">{t('requests.currency')}:</div>
              <div className="detail-value">{bid.currency || bid.request?.currency || 'EUR'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">{t('bids.liquidityAvailable')}:</div>
              <div className="detail-value">{formatCurrencyWithSymbol(bid.request.amount, bid.request?.currency || bid.currency || 'EUR')}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">{t('bids.bidAmount')}:</div>
              <div className="detail-value">{formatCurrencyWithSymbol(bid.amount, bid.currency || bid.request?.currency || 'EUR')}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">{t('bids.minimumAllocation')}:</div>
              <div className="detail-value">{formatCurrencyWithSymbol(bid.minimumAllocation, bid.currency || bid.request?.currency || 'EUR')}</div>
            </div>

            {/* Interest Available for Rolling */}
            {bid.interestAvailableForRolling > 0 && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.interestAvailableForRolling')}:</div>
                <div className="detail-value">{formatCurrencyWithSymbol(bid.interestAvailableForRolling, bid.currency || bid.request?.currency || 'EUR')}</div>
              </div>
            )}

            <div className="detail-item">
              <div className="detail-label">{t('requests.interestRateType')}:</div>
              <div className="detail-value">{rateType === 'floating' ? t('requests.floating') : t('requests.fixed')}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">{rateType === 'floating' ? t('bids.interestRateFloating').replace(' (%)', '') + ':' : t('bids.interestRate') + ':'}</div>
              <div className="detail-value">{formatInterestRate(bid.interestRate, rateType, bid.baseRate)}</div>
            </div>
            
            {/* Show base rate for floating rates */}
            {rateType === 'floating' && bid.baseRate && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.baseRate')}:</div>
                <div className="detail-value">{formatBaseRate(bid.baseRate)}</div>
              </div>
            )}
            
            <div className="detail-item">
              <div className="detail-label">{t('requests.maturity')}:</div>
              <div className="detail-value">{formatDuration(bid.request.duration, t, bid.request.customStartDate, bid.request.customEndDate)}</div>
            </div>
            {bid.request.duration === 'custom' && bid.request.customStartDate && (
              <div className="detail-item">
                <div className="detail-label">{t('requests.customStartDate')}:</div>
                <div className="detail-value">{new Date(bid.request.customStartDate).toLocaleDateString()}</div>
              </div>
            )}
            {bid.request.duration === 'custom' && bid.request.customEndDate && (
              <div className="detail-item">
                <div className="detail-label">{t('requests.customEndDate')}:</div>
                <div className="detail-value">{new Date(bid.request.customEndDate).toLocaleDateString()}</div>
              </div>
            )}
            {/* Withdrawal Type — nascosto per hot_money */}
            {bid.request.requestType !== 'hot_money' && (
              <div className="detail-item">
                <div className="detail-label">{t('requests.withdrawalType')}:</div>
                <div className="detail-value">
                  {bid.request.withdrawalType === 'demand' && t('requestDetail.withdrawal.demand')}
                  {bid.request.withdrawalType === 'early_allowed' && t('requests.earlyWithdrawalAllowed')}
                  {bid.request.withdrawalType === 'no_withdrawal' && t('requests.noWithdrawalAllowed')}
                </div>
              </div>
            )}

            {/* Campi condizionali per early withdrawal */}
            {bid.request.withdrawalType === 'early_allowed' && bid.noticePeriod && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.yourNoticePeriod')}:</div>
                <div className="detail-value">{bid.noticePeriod} {bid.noticePeriod === 1 ? t('common.days').slice(0, -1) : t('common.days')}</div>
              </div>
            )}
            
            {bid.request.withdrawalType === 'early_allowed' && typeof bid.penaltyApplies !== 'undefined' && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.withdrawalPenalty')}:</div>
                <div className="detail-value">{bid.penaltyApplies ? t('common.yes') : t('common.no')}</div>
              </div>
            )}
            
            {bid.penaltyApplies && bid.penaltyDescription && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.penaltyTerms')}:</div>
                <div className="detail-value">{bid.penaltyDescription}</div>
              </div>
            )}
            
            {/* Support Account Field */}
            <div className="detail-item">
              <div className="detail-label">{t('bids.supportAccount')}:</div>
              <div className="detail-value">{bid.supportAccount ? t('common.yes') : t('common.no')}</div>
            </div>

            {/* Renewal flag */}
            {bid.request?.isRenewal && (
              <div className="detail-item">
                <div className="detail-label">{t('requests.renewal')}:</div>
                <div className="detail-value">
                  <span style={{ color: '#2e7d32', fontWeight: '500' }}>
                    {t('requests.renewalFlagActive')}
                  </span>
                </div>
              </div>
            )}

            {/* Company Notes */}
            {bid.request?.notes && (
              <div className="detail-item">
                <div className="detail-label">{t('requests.companyNotes')}:</div>
                <div className="detail-value">{bid.request.notes}</div>
              </div>
            )}
            
            {/* Bank Notes */}
            {bid.notes && (
              <div className="detail-item">
                <div className="detail-label">{t('requests.additionalNotes')}:</div>
                <div className="detail-value">{bid.notes}</div>
              </div>
            )}
            {/* Created By - ultimo campo */}
            <div className="detail-item">
              <div className="detail-label">{t('bids.createdBy')}:</div>
              <div className="detail-value">{bid.createdBy ? `${bid.createdBy.firstName} ${bid.createdBy.lastName}` : t('bids.notAvailable')}</div>
            </div>

          </div>
        </div>
        
        {/* Alert based on bid status */}
        {bid.status === 'pending' && (
          <div className="alert-info">
            <p>{t('bids.bidPendingReview')}</p>
          </div>
        )}
        
        {bid.status === 'rejected' && (
          <div className="alert-danger">
            <p>{t('bids.bidRejectedMessage')}</p>
          </div>
        )}
        
        {bid.status === 'cancelled' && (
          <div className="alert-danger">
            <p>{t('bids.bidCancelledMessage')}</p>
          </div>
        )}
        
        {bid.status === 'approved' && (
          <div className="alert-success">
            <p>
              {t('bids.bidApprovedMessage')}
            </p>
          </div>
        )}
        
        {/* Allocation details for approved bids */}
        {bid.status === 'approved' && (
          <div className="bank-bid" style={{ marginBottom: '25px', marginTop: '20px'}}>
            <h3 style={{marginBottom: '10'}}>{t('bids.allocationDetails')}</h3>
            <div className="detail-item">
              <div className="detail-label">{t('bids.allocatedAmount')}:</div>
              <div className="detail-value">{formatCurrencyWithSymbol(getAllocationAmount(), bid.allocation?.currency || bid.currency || bid.request?.currency || 'EUR')}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">
                {rateType === 'floating' ? t('bids.interestRateFloating').replace(' (%)', '') + ':' : t('bids.interestRate') + ':'}
              </div>
              <div className="detail-value">
                {formatInterestRate(bid.interestRate, rateType, bid.baseRate)}
              </div>
            </div>
            {/* Show base rate in allocation details if exists and rate is floating */}
            {rateType === 'floating' && bid.baseRate && (
              <div className="detail-item">
                <div className="detail-label">{t('bids.baseRate')}:</div>
                <div className="detail-value">{formatBaseRate(bid.baseRate)}</div>
              </div>
            )}
            <div className="detail-item">
              <div className="detail-label">{t('common.date')}:</div>
              <div className="detail-value">
                {bid.allocation 
                  ? new Date(bid.allocation.startDate).toLocaleDateString()
                  : t('bids.transferPending')}
              </div>
            </div>
            
            {/* Transfer confirmation button */}
            {bid.allocation?.transferStatus === 'pending' && (
              <div className="transfer-actions">
                <button
                  className="btn btn-primary"
                  onClick={confirmTransfer}
                >
                  {t('bids.confirmTransfer')}
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Only show action buttons for pending bids, and only the cancel button */}
        {bid.status === 'pending' && (
          <div className="bid-actions">
            <button
              type="button"
              className="btn btn-danger"
              onClick={cancelBid}
            >
              {t('bids.cancelBid')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BidDetail;